import React, { Component } from 'react';
import './RoutesMenu.scss';
import { Dayjs } from 'dayjs';
import { Region } from '../../types/orderTypes';
import {
  BundleRoute,
  BundleRoutes,
  BundleRoutesMetrics,
} from '../../types/bundleTypes';
import RouteInfoList from './RouteInfoList';
import { sum } from '../../utils/array';
import { formatTimePerStop } from './RouteUtils';

interface State {
  unselectedRoutes: string[];
  ordersCount: number;
}

interface Props {
  date: Dayjs;
  region: Region;
  bundleRoutes?: BundleRoutes;
  metrics?: BundleRoutesMetrics;
  onChangeVisibleRoutes: (routeIds: string[]) => void;
}

export default class RoutesMenu extends Component<Props, State> {
  private get routes() {
    return this.props.bundleRoutes?.routes || [];
  }

  constructor(props: Props) {
    super(props);
    this.state = {
      unselectedRoutes: [],
      ordersCount: 0,
    };
  }

  componentDidUpdate(prevProps: Readonly<Props>, prevState: State) {
    const prevRoutes = prevProps.bundleRoutes?.routes || [];

    if (
      this.routes.map(r => r.id).join(',') ===
      prevRoutes.map(r => r.id).join(',')
    ) {
      return;
    }

    this.calculateStats(this.routes);
  }

  calculateStats(routes: BundleRoute[]) {
    const ordersBundleCount = routes.map(
      route => new Set(route.steps.map(s => s.orderId)).size
    );
    const orderCount = sum(ordersBundleCount);
    this.setState({
      ordersCount: orderCount,
    });
  }

  deselectRoute = (id: string) => {
    this.setState(
      {
        unselectedRoutes: [...this.state.unselectedRoutes, id],
      },
      () => this.refreshVisibleRoutes()
    );
  };

  selectRoute = (id: string) => {
    const { unselectedRoutes } = this.state;
    const index = unselectedRoutes.findIndex(routeId => routeId === id);

    this.setState(
      {
        unselectedRoutes: [
          ...unselectedRoutes.slice(0, index),
          ...unselectedRoutes.slice(index + 1),
        ],
      },
      () => this.refreshVisibleRoutes()
    );
  };

  selectAll = (selectAll: boolean) => {
    if (selectAll) {
      this.setState(
        {
          unselectedRoutes: [],
        },
        () => this.refreshVisibleRoutes()
      );
    } else {
      this.setState(
        {
          unselectedRoutes: this.routes.map(route => route.id),
        },
        () => this.refreshVisibleRoutes()
      );
    }
  };

  refreshVisibleRoutes() {
    const visibleRoutes = this.routes
      .map(r => r.id)
      .filter(routeId => !this.state.unselectedRoutes.includes(routeId));
    this.props.onChangeVisibleRoutes(visibleRoutes);
  }

  formatCounter(value: number): string {
    const val = Math.floor(value * 10) / 10;
    return value <= 0 ? '-' : `${val}`;
  }

  render() {
    const { unselectedRoutes, ordersCount } = this.state;
    const { metrics } = this.props;
    const selectedRoutes = this.routes
      .map(route => route.id)
      .filter(id => !unselectedRoutes.includes(id));
    const allSelected = unselectedRoutes.length === 0;

    return (
      <div>
        {this.routes.length > 0 && (
          <div className="routes__filter-summary">
            <div className="routes__filter-summary__row">
              <div>
                <span className="routes__filter-summary__label">
                  Bundle ID:
                </span>
                <span className="routes__filter-summary__value">
                  {this.props.bundleRoutes.id}
                </span>
              </div>
            </div>
            <div className="routes__filter-summary__row">
              <div>
                <span className="routes__filter-summary__label">Orders:</span>
                <span className="routes__filter-summary__value">
                  {ordersCount}
                </span>
              </div>
              <div>
                <span className="routes__filter-summary__label">Bundles:</span>
                <span className="routes__filter-summary__value">
                  {this.routes.length}
                </span>
              </div>
            </div>
            <div className="routes__filter-summary__stop-times">
              <span>Orders per bundle:</span>
              <span>
                Min:{' '}
                <span className="routes__filter-summary__value">
                  {this.formatCounter(metrics?.ordersPerBundle.minimum || 0)}
                </span>
              </span>
              <span>
                Average:{' '}
                <span className="routes__filter-summary__value">
                  {this.formatCounter(metrics?.ordersPerBundle.average || 0)}
                </span>
              </span>
              <span>
                Max:{' '}
                <span className="routes__filter-summary__value">
                  {this.formatCounter(metrics?.ordersPerBundle.maximum || 0)}
                </span>
              </span>
            </div>
            <div className="routes__filter-summary__stop-times">
              <span>Time per stop:</span>
              <span>
                Min:{' '}
                <span className="routes__filter-summary__value">
                  {formatTimePerStop(metrics?.timePerStopSeconds.minimum || 0)}
                </span>
              </span>
              <span>
                Average:{' '}
                <span className="routes__filter-summary__value">
                  {formatTimePerStop(metrics?.timePerStopSeconds.average || 0)}
                </span>
              </span>
              <span>
                Max:{' '}
                <span className="routes__filter-summary__value">
                  {formatTimePerStop(metrics?.timePerStopSeconds.maximum || 0)}
                </span>
              </span>
            </div>
            <div className="routes__filter-summary__row">
              <div>
                <span className="routes__filter-summary__label">
                  ETA Outside window:
                </span>
                <span className="routes__filter-summary__value">
                  {metrics?.ordersWithEtaOutsideWindowCount || 0}
                </span>
              </div>
              <div>
                <span className="routes__filter-summary__label">
                  Average ETA time outside window:
                </span>
                <span className="routes__filter-summary__value">
                  {formatTimePerStop(metrics?.avgEtaOutsideWindowSeconds || 0)}
                </span>
              </div>
            </div>
            <div className="routes__filter-summary__row">
              <div>
                <span className="routes__filter-summary__label">
                  Delivered Outside window:
                </span>
                <span className="routes__filter-summary__value">
                  {metrics?.ordersWithDeliveryOutsideWindowCount || 0}
                </span>
              </div>
              <div>
                <span className="routes__filter-summary__label">
                  Avg time outside window:
                </span>
                <span className="routes__filter-summary__value">
                  {formatTimePerStop(
                    metrics?.avgDeliveryOutsideWindowSeconds || 0
                  )}
                </span>
              </div>
            </div>
          </div>
        )}
        <div className="routes__filter-menu-header">
          <label onClick={e => e.stopPropagation()} htmlFor="selectAllRoutes">
            <input
              className="route-info-input-checkbox"
              id="selectAllRoutes"
              type="checkbox"
              checked={allSelected}
              onChange={event => this.selectAll(event.target.checked)}
            />
            <h4>Routes selected</h4>
          </label>
          <h5>{this.routes.length <= 0 ? '' : selectedRoutes.length}</h5>
        </div>

        <div style={{ height: 10 }} />
        <RouteInfoList
          routes={this.routes}
          selectedRoutes={selectedRoutes}
          onDeselectRoute={id => this.deselectRoute(id)}
          onSelectRoute={id => this.selectRoute(id)}
        />
      </div>
    );
  }
}
